import { ErrorHandler, Injectable, isDevMode } from '@angular/core';
import { Scope, User } from "@sentry/types";
import { IUser } from "libraries/interfaces/IUser";
import { Yii } from "libraries/interfaces/Yii";
import { VCS_PARENTS } from "app/revision.generated";
import { wenji } from "libraries/interfaces/IWenji";
import * as moment from 'moment';
import { EnvironmentNameService } from "libraries/service/error-tracing/environment-name.service";

// tslint:disable-next-line:no-any
declare const window: any;

@Injectable()
export class WuiErrorHandlerService implements ErrorHandler {

    private globalLastCapture: moment.Moment = null;
    private readonly capturedErrorMessages: { [msg: string]: moment.Moment } = {};

    constructor(private readonly environmentNameService: EnvironmentNameService) {
        const sentryDsn: string = wenji.sentry ? wenji.sentry.dsn : null;

        import('@sentry/browser').then((Sentry) => {
            // initialize sentry
            /** Sentry.BrowserOptions */
            let sentryOptions: object;
            if (sentryDsn && !isDevMode()) {
                // tslint:disable-next-line:no-console
                console.info("Sentry is enabled.");
                sentryOptions = {
                    dsn: sentryDsn,
                    environment: environmentNameService.getEnvironment(window.location.hostname, sessionStorage.getItem("$instance_url")),
                    release: getVcsRelease(),
                    enabled: true,
                };
            } else {
                // tslint:disable-next-line:no-console
                console.info("Sentry is disabled because of angular dev mode or missing 'wenji.sentry.dsn'.");
                sentryOptions = {
                    enabled: false,
                };
            }
            Sentry.init(sentryOptions);

            // set additional tags
            Sentry.configureScope(((scope: Scope): void => {
                scope.setTags({
                    'vcs.revision': getVcsRevision(),
                    'vcs.date': getVcsDate(),
                    'server.timezone': wenji.ui.timezone
                });
            }));
        })
            .catch((e: Error) => {
                console.error("Sentry import error", e);
            });
    }

    public handleError(error: Error): void {
        const lastCapture: moment.Moment = this.capturedErrorMessages[error.message];

        const isIconError: boolean = error?.message && error.message.includes('fa-icon');
        // capture if error never hapened, or hapened more than 10 seconds ago
        if (is10SecondsAfter(lastCapture) && !isIconError) {

            import('@sentry/browser').then((Sentry) => {
                // capture exception silently
                const eventId: string = Sentry.captureException(error);
                this.globalLastCapture = moment();
                this.capturedErrorMessages[error.message] = this.globalLastCapture;

                // if error never captured and showReportDialog enabled, show report dialog
                if (!lastCapture && is10SecondsAfter(this.globalLastCapture) && wenji.sentry && wenji.sentry.showReportDialog) {
                    const userInfoStr: string = sessionStorage.getItem('userInformations');
                    const userInfo: IUser = userInfoStr ? JSON.parse(userInfoStr) : null;
                    const sentryUser: User = userInfo ? {
                        email: userInfo.sam_contact.email,
                        name: `${userInfo.sam_contact.firstName} ${userInfo.sam_contact.lastName}`
                    } : null;
                    Sentry.showReportDialog({
                        eventId,
                        user: sentryUser,
                        title: Yii.t('wui-shared-js', 'Nous rencontrons des difficultés...')
                    });
                }
            })
                .catch((e: Error) => {
                    console.error("Sentry import error", e);
                });
        }

        // always log error in console
        if(isIconError){
            console.error('Icon error', error);
        }else{
            console.error('Sentry handled error.', error);
        }
    }
}

function getVcsRelease(): string {
    const parent: object = VCS_PARENTS[0];
    // tslint:disable-next-line:no-string-literal
    return `${parent['branch']}@${parent['tags'].filter((str: string): boolean => str !== 'tip').join(',')}`;
}

function getVcsDate(): string {
    const dateArray: number[] = VCS_PARENTS[0].date;
    const unixTimestamp: number = dateArray[0];
    const offsetSeconds: number = dateArray[1];
    const vcsDateStr: string = moment.unix(unixTimestamp).utcOffset(offsetSeconds / 60 / 60 * -1, true).toISOString(true);
    return vcsDateStr;
}

function getVcsRevision(): string {
    return VCS_PARENTS[0].node;
}

function is10SecondsAfter(lastCapture?: moment.Moment): boolean {
    const now: moment.Moment = moment();
    return !lastCapture || lastCapture.clone().add(10, 'second').isBefore(now);
}
